// src/pages/tournament/useTournament.js
// Everything the tournament page needs from the backend: the players and rounds, and one function
// per action. Failures are reported with a toast; each action resolves to a truthy value on success.
import { useCallback, useEffect, useState } from 'react';
import { apiRequest } from '../../api';
import { useDataChanged } from '../../testMode/testMode';
import { useToast } from '../../components/Toast';
import { updateMatch, updateMatchup, updateSet } from './roundsState';

export function useTournament(tournamentId, user) {
    const toast = useToast();
    const userId = user?.uid;
    const [players, setPlayers] = useState([]);
    const [rounds, setRounds] = useState([]);
    const [fixedTeams, setFixedTeams] = useState([]); // only used in the 'fixed-teams' format
    const [loading, setLoading] = useState(true);
    const [details, setDetails] = useState({ name: `Tournament ${tournamentId}` });
    // Goes up whenever something the ranking depends on changes, so views showing it can refetch.
    const [version, setVersion] = useState(0);
    const bump = useCallback(() => setVersion((current) => current + 1), []);

    const base = `/tournaments/${tournamentId}`;
    const call = useCallback(
        (method, path, body) => apiRequest(method, `${base}${path}`, userId, body),
        [base, userId]
    );

    const load = useCallback(async () => {
        try {
            const [loadedPlayers, loadedRounds, loadedTournament, loadedTeams] = await Promise.all([
                call('GET', '/players'),
                call('GET', '/rounds'),
                call('GET', '').catch(() => null), // the name is nice to have; players and rounds are what matter
                call('GET', '/teams').catch(() => []), // only used in the 'fixed-teams' format
            ]);
            setPlayers(loadedPlayers);
            setRounds(loadedRounds);
            if (loadedTournament?.name) setDetails(loadedTournament);
            setFixedTeams(loadedTeams);
            bump();
        } catch (error) {
            console.error('Error loading the tournament:', error);
            toast.error('Could not load this tournament.');
        } finally {
            setLoading(false);
        }
    }, [call, toast, bump]);

    useEffect(() => {
        if (userId) load();
    }, [userId, load]);

    // Breaks, games played and the ranking depend on the rounds and scores, so the players are fetched
    // again after those change. Quiet on failure: the next full load fixes it.
    const refreshPlayers = useCallback(async () => {
        try {
            setPlayers(await call('GET', '/players'));
        } catch (error) {
            console.error('Error refreshing the players:', error);
        }
        bump();
    }, [call, bump]);

    // The test panel changes data behind our back.
    useDataChanged(() => {
        if (userId) load();
    });

    // Runs an action; on failure shows a toast and resolves to undefined.
    const attempt = useCallback(
        async (description, action) => {
            try {
                return await action();
            } catch (error) {
                console.error(`Error trying to ${description}:`, error);
                toast.error(`Could not ${description}. ${error.message}`);
                return undefined;
            }
        },
        [toast]
    );

    // ---- the tournament itself ----
    const saveTournament = (description, changes) =>
        attempt(description, async () => {
            const updated = await call('PATCH', '', changes);
            setDetails(updated);
            return updated;
        });

    const renameTournament = (newName) => saveTournament('rename the tournament', { name: newName });

    // name, startDate, endDate, notes and/or courts
    const updateDetails = async (changes) => {
        const updated = await saveTournament('save the tournament details', changes);
        if (updated && 'teamsPerGroup' in changes) refreshGroups(); // the teams were dealt again
        return updated;
    };

    // The ranking and matchmaking methods to choose from (with what each is good and bad at).
    const getAlgorithms = () => attempt('load the ranking and matchmaking methods', () => apiRequest('GET', '/algorithms', userId));

    // Changing the ranking method re-ranks everybody, so what shows the ranking is refreshed.
    const updateAlgorithms = (changes) =>
        attempt('save the ranking and matchmaking settings', async () => {
            const updated = await call('PATCH', '', changes);
            setDetails(updated);
            refreshPlayers();
            return updated;
        });

    // A fresh copy for a rematch: same courts and singles setting, same active players, nothing played.
    const duplicateTournament = () => attempt('duplicate the tournament', () => call('POST', '/duplicate', {}));

    // Everything needed to keep a copy of the tournament outside the app: fetched fresh, not from state,
    // so it is complete and up to date even if the ranking tab was never opened.
    const exportData = () =>
        attempt('export the tournament', async () => {
            const [exportedPlayers, exportedRounds, ranking] = await Promise.all([
                call('GET', '/players'),
                call('GET', '/rounds'),
                call('GET', '/ranking'),
            ]);
            return {
                tournament: details,
                players: exportedPlayers,
                rounds: exportedRounds,
                standings: ranking.standings,
                exportedAt: new Date().toISOString(),
            };
        });

    // The read-only link for participants: created on demand, and gone for good when sharing stops.
    const startSharing = () =>
        attempt('create the share link', async () => {
            const { shareToken } = await call('POST', '/share', {});
            setDetails((prev) => ({ ...prev, shareToken }));
            return shareToken;
        });

    const stopSharing = () =>
        attempt('stop sharing', async () => {
            await call('DELETE', '/share', {});
            setDetails((prev) => ({ ...prev, shareToken: null }));
            return true;
        });

    // Players from the user's earlier tournaments matching the words, for adding them again.
    const searchPreviousPlayers = useCallback(
        (query) => apiRequest('GET', `/playerHistory?q=${encodeURIComponent(query)}&tournamentId=${tournamentId}`, userId),
        [userId, tournamentId]
    );

    // ---- players ----
    const addPlayer = (firstName, lastName, startLevel) =>
        attempt('add the player', async () => {
            const player = await call('POST', '/players', { firstName, lastName, ...(startLevel ? { startLevel } : {}) });
            setPlayers((prev) => [...prev, player]);
            bump();
            return player;
        });

    // Inactive players are left out of new rounds but stay listed (and in the ranking).
    const setPlayerActive = (playerId, active) =>
        attempt(active ? 'activate the player' : 'deactivate the player', async () => {
            const updated = await call('PATCH', `/players/${playerId}`, { active });
            setPlayers((prev) => prev.map((player) => (player.playerId === playerId ? { ...player, ...updated } : player)));
            bump();
            return updated;
        });

    // What the organizer says about a player before there are results; only the planner uses it ('' clears it).
    const setPlayerLevel = (playerId, startLevel) =>
        attempt('set the level', async () => {
            const updated = await call('PATCH', `/players/${playerId}`, { startLevel: startLevel || null });
            setPlayers((prev) => prev.map((player) => (player.playerId === playerId ? { ...player, ...updated } : player)));
            bump();
            return updated;
        });

    // Corrects a player's name; resolves to true when it was saved.
    const renamePlayer = (playerId, firstName, lastName) =>
        attempt('rename the player', async () => {
            const updated = await call('PATCH', `/players/${playerId}`, { firstName, lastName });
            setPlayers((prev) => prev.map((player) => (player.playerId === playerId ? { ...player, ...updated } : player)));
            bump();
            return true;
        });

    const deletePlayer = (playerId) =>
        attempt('delete the player', async () => {
            await call('DELETE', `/players/${playerId}`);
            setPlayers((prev) => prev.filter((player) => player.playerId !== playerId));
            bump();
            return true;
        });

    // Reading the file happens on the device (works offline); adding each player then goes through the normal
    // (also offline-capable) call, one by one, the same as typing them in.
    const uploadPlayers = (file) =>
        attempt('upload the players', async () => {
            const { playersFromExcelFile } = await import('../../utils/excelPlayers');
            const rows = await playersFromExcelFile(file);
            const added = [];
            for (const row of rows) {
                added.push(await call('POST', '/players', {
                    firstName: row.firstName, lastName: row.lastName, ...(row.startLevel ? { startLevel: row.startLevel } : {}),
                }));
            }
            setPlayers((prev) => [...prev, ...added]);
            bump();
            toast.success(`Added ${added.length} player${added.length === 1 ? '' : 's'} from the file.`);
            return added;
        });

    // ---- fixed teams (the 'fixed-teams' format: pairs stay the same for the whole tournament) ----
    // In 'groups-finals' the number of groups follows from the number of teams, and a change in it deals the
    // teams over the groups again: so the teams and the tournament (its group count) are fetched again.
    const refreshGroups = useCallback(async () => {
        try {
            const [loadedTeams, loadedTournament] = await Promise.all([call('GET', '/teams'), call('GET', '')]);
            setFixedTeams(loadedTeams);
            if (loadedTournament?.name) setDetails(loadedTournament);
        } catch (error) {
            console.error('Error refreshing the groups:', error);
        }
    }, [call]);

    const addFixedTeam = (playerIds) =>
        attempt('make the team', async () => {
            const team = await call('POST', '/teams', { playerIds });
            setFixedTeams((prev) => [...prev, team]);
            if (details.format === 'groups-finals') refreshGroups();
            return team;
        });

    const deleteFixedTeam = (teamId) =>
        attempt('remove the team', async () => {
            await call('DELETE', `/teams/${teamId}`);
            setFixedTeams((prev) => prev.filter((team) => team.teamId !== teamId));
            if (details.format === 'groups-finals') refreshGroups();
            return true;
        });

    // Which group a team plays in ('groups-finals' format); moving one is locked once the first round exists.
    const moveFixedTeam = (teamId, group) =>
        attempt('move the team', async () => {
            const moved = await call('PATCH', `/teams/${teamId}`, { group });
            setFixedTeams((prev) => prev.map((team) => (team.teamId === teamId ? { ...team, ...moved } : team)));
            return moved;
        });

    // Deals the teams over the groups again, at random.
    const regroupTeams = () =>
        attempt('shuffle the groups', async () => {
            const teams = await call('POST', '/teams/regroup', {});
            setFixedTeams(teams);
            return teams;
        });

    // ---- rounds ----
    // In 'groups-finals' the name of the next round (and whether the final is done) comes with the tournament.
    const refreshDetails = useCallback(async () => {
        try {
            const loaded = await call('GET', '');
            if (loaded?.name) setDetails(loaded);
        } catch (error) {
            console.error('Error refreshing the tournament:', error);
        }
    }, [call]);

    // `requests` are pairings the players asked for (see RequestsEditor): granted first, the rest is planned around them
    const addRound = (requests) =>
        attempt('create the round', async () => {
            const round = await call('POST', '/rounds', requests && requests.length > 0 ? { requests } : undefined);
            setRounds((prev) => [...prev, round]);
            refreshPlayers();
            if (details.format === 'groups-finals') refreshDetails();
            return round;
        });

    const deleteRound = (roundId) =>
        attempt('delete the round', async () => {
            await call('DELETE', `/rounds/${roundId}`);
            setRounds((prev) => prev.filter((round) => round.roundId !== roundId));
            refreshPlayers();
            if (details.format === 'groups-finals') refreshDetails();
            return true;
        });

    // ---- matches and sets ----
    const matchupPath = (roundId, matchupId) => `/rounds/${roundId}/matchups/${matchupId}`;

    // Cancelling counts the matchup as a break for its players instead of a game; it stays in the round,
    // greyed out. Reinstating undoes it. Both change breaks/games played, so the players are refreshed.
    const cancelMatchup = (roundId, matchupId) =>
        attempt('cancel the matchup', async () => {
            const updated = await call('POST', `${matchupPath(roundId, matchupId)}/cancel`, {});
            setRounds((prev) => updateMatchup(prev, roundId, matchupId, (matchup) => ({ ...matchup, cancelled: updated.cancelled })));
            refreshPlayers();
            if (details.format === 'groups-finals') refreshDetails();
            return updated;
        });

    const reinstateMatchup = (roundId, matchupId) =>
        attempt('reinstate the matchup', async () => {
            const updated = await call('DELETE', `${matchupPath(roundId, matchupId)}/cancel`, {});
            setRounds((prev) => updateMatchup(prev, roundId, matchupId, (matchup) => ({ ...matchup, cancelled: updated.cancelled })));
            refreshPlayers();
            if (details.format === 'groups-finals') refreshDetails();
            return updated;
        });

    const addMatch = (roundId, matchupId) =>
        attempt('add the match', async () => {
            const match = await call('POST', `${matchupPath(roundId, matchupId)}/matches`);
            setRounds((prev) =>
                updateMatchup(prev, roundId, matchupId, (matchup) => ({
                    ...matchup,
                    matches: [...(matchup.matches || []), match],
                }))
            );
            return match;
        });

    const deleteMatch = (roundId, matchupId, matchId) =>
        attempt('delete the match', async () => {
            await call('DELETE', `${matchupPath(roundId, matchupId)}/matches/${matchId}`);
            setRounds((prev) =>
                updateMatchup(prev, roundId, matchupId, (matchup) => ({
                    ...matchup,
                    matches: matchup.matches.filter((match) => match.matchId !== matchId),
                }))
            );
            refreshPlayers();
            return true;
        });

    const addSet = (roundId, matchupId, matchId) =>
        attempt('add the set', async () => {
            const set = await call('POST', `${matchupPath(roundId, matchupId)}/matches/${matchId}/sets`);
            setRounds((prev) =>
                updateMatch(prev, roundId, matchupId, matchId, (match) => ({
                    ...match,
                    sets: [...(match.sets || []), set],
                }))
            );
            return set;
        });

    const deleteSet = (roundId, matchupId, matchId, setId) =>
        attempt('delete the set', async () => {
            await call('DELETE', `${matchupPath(roundId, matchupId)}/matches/${matchId}/sets/${setId}`);
            setRounds((prev) =>
                updateMatch(prev, roundId, matchupId, matchId, (match) => ({
                    ...match,
                    sets: match.sets.filter((set) => set.setId !== setId),
                }))
            );
            refreshPlayers();
            return true;
        });

    const saveScore = (roundId, matchupId, matchId, setId, team1, team2) =>
        attempt('save the score', async () => {
            const saved = await call('PATCH', `${matchupPath(roundId, matchupId)}/matches/${matchId}/sets/${setId}`, {
                team1,
                team2,
            });
            setRounds((prev) =>
                updateSet(prev, roundId, matchupId, matchId, setId, (set) => ({
                    ...set,
                    team1Score: saved.team1Score,
                    team2Score: saved.team2Score,
                    updatedAt: saved.updatedAt,
                }))
            );
            refreshPlayers();
            return saved;
        });

    // Every game this player has played in this tournament, with the breakdown behind each one's score.
    const getPlayerGames = (playerId) => attempt("load this player's games", () => call('GET', `/players/${playerId}/games`));

    return {
        name: details.name,
        details,
        version,
        renameTournament,
        updateDetails,
        duplicateTournament,
        getAlgorithms,
        updateAlgorithms,
        exportData,
        startSharing,
        stopSharing,
        searchPreviousPlayers,
        getPlayerGames,
        players,
        rounds,
        fixedTeams,
        loading,
        addPlayer,
        setPlayerActive,
        setPlayerLevel,
        renamePlayer,
        deletePlayer,
        uploadPlayers,
        addFixedTeam,
        deleteFixedTeam,
        moveFixedTeam,
        regroupTeams,
        addRound,
        deleteRound,
        cancelMatchup,
        reinstateMatchup,
        addMatch,
        deleteMatch,
        addSet,
        deleteSet,
        saveScore,
    };
}
